import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import * as store from '../../reducers/store';
import * as rootReducer from '../../reducers/root';

import { IntroPage } from './IntroPage';
import Loader from '../../components/Loader';
import NavBar, { View } from '../../components/NavBar';

import { sendEventMessage, Message } from '../../utils/native';
import { withFetchPatient } from '../../components/hoc/withFetchPatient';
import { FormTypes, getFormsByType, startForm } from '../../utils/form';
import { getFormsFromFormSets } from '../../utils/formSet';
import { useReviewRedirect } from '../../hooks/review/useReviewRedirect';

import styles from './index.module.css';

///////////////////////////////////////////////////
//////COMPONENT

export const FormStartComponent: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { authenticationCode } = useParams<{ authenticationCode: string }>();
  const formSets = useSelector((state: store.Loaded) => state.formSets);
  const { practice } = Object.values(formSets)[0];
  const masterPracticePreference = useSelector(
    (state: store.Loaded) => state.masterPracticePreference,
  );
  const skipIntroSingleForm =
    masterPracticePreference.previsitSkipIntroSingleForm;
  const multiFormSet = useSelector((state: store.All) => state.multiFormSet);
  const showArrivalTimeOnly = useSelector(
    (state: store.Loaded) => state.masterPracticePreference.showArrivalTimeOnly,
  );
  const hideEncounterStartTimeByVisitKind = useSelector(
    (state: store.Loaded) =>
      state.masterPracticePreference.hideEncounterStartTimeByVisitKind,
  );
  const sortedSets = useMemo(() => {
    return _.sortBy(formSets, 'encounter.startTime');
  }, [formSets]);

  useEffect(() => {
    sendEventMessage(Message.InitialPageLoadStarted);
    sendEventMessage(Message.InitialPageLoadCompleted);
  }, []);

  useReviewRedirect(authenticationCode);

  const forms = getFormsFromFormSets(formSets);
  const formIds = Object.keys(forms);
  const hasOnlySingleForm = formIds.length === 1;
  const [pending, setPending] = useState(false);
  const allCompleted = !formIds.length;

  useEffect(() => {
    if (!hasOnlySingleForm || !skipIntroSingleForm) return;
    const onlyFormId = formIds[0];
    const onlyForm = forms[onlyFormId];
    const onlyFormSetId = onlyForm.fields[0]?.previsitFormSetId;
    if (!onlyFormId || !onlyForm || !onlyFormSetId || pending) return;
    setPending(true);
    startForm({
      history,
      authenticationCode,
      formSetId: onlyFormSetId,
      form: onlyForm,
      multiFormSet,
      redirect: true,
      setFormStatus: ({ formSetId, formId, status }) =>
        dispatch(rootReducer.setFormStatus({ formSetId, formId, status })),
    });
  }, [
    history,
    hasOnlySingleForm,
    authenticationCode,
    formIds,
    formSets,
    forms,
    multiFormSet,
    skipIntroSingleForm,
    dispatch,
    pending,
  ]);

  if (pending) return <Loader />;

  if (!practice) {
    alert(
      'Uh oh, something went wrong loading your form. Please refresh the page.',
    );
    return null;
  }
  return (
    <>
      <NavBar view={View.FormStart} />
      <div className={styles.FormContainer}>
        <div className={styles.Form}>
          <div className={styles.PageWrapper}>
            {allCompleted && (
              <label className={styles.Label}>
                All done, we’ll see you soon!
              </label>
            )}
            {Object.values(sortedSets).map((formSet, idx) => {
              return (
                <IntroPage
                  key={`intro-list-${idx}`}
                  encounter={formSet.encounter || null}
                  practice={formSet.practice ?? undefined}
                  provider={formSet.provider ?? undefined}
                  authenticationCode={authenticationCode}
                  label={idx === 0 ? formSet.practicePreference.introLabel : ''}
                  forms={getFormsByType(formSet.forms, FormTypes.Main)}
                  documents={formSet.documents}
                  multi={multiFormSet}
                  formSetId={formSet.id}
                  showArrivalTimeOnly={showArrivalTimeOnly}
                  hideEncounterStartTimeByVisitKind={
                    hideEncounterStartTimeByVisitKind
                  }
                  providerNameDisplays={
                    masterPracticePreference.previsitProviderNameDisplays
                  }
                />
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export const FormStart = withFetchPatient(FormStartComponent);
